'use client';

import { useState, useEffect, use } from 'react';
import Link from 'next/link';
import Breadcrumb from '@/components/ui/Breadcrumb';
import NavBar from '../../home-page/components/NavBar';
import FooterSection from '../../home-page/components/FooterSection';
import { generateBreadcrumbSchema } from '@/lib/utils/metadata';
import { blogService } from '@/lib/services/blogService';
import { trackEvent } from '@/lib/analytics';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || '';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  author: string;
  createdAt: string;
  category: string;
  tags?: string[];
  keywords?: string;
  internalLinks?: InternalLink[];
  scheduledAt?: string;
  publishedAt?: string;
  featuredImageUrl?: string;
  featuredImageAlt?: string;
}

interface InternalLink {
  url: string;
  title: string;
}

interface PageProps {
  params: Promise<{ slug: string }>;
}

interface TocItem {
  id: string;
  text: string;
  level: number;
}

export default function BlogPostPage({ params }: PageProps) {
  const resolvedParams = use(params);
  const postSlug = resolvedParams.slug;
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [relatedLoading, setRelatedLoading] = useState(true);
  const [tableOfContents, setTableOfContents] = useState<TocItem[]>([]);
  const [activeHeading, setActiveHeading] = useState<string>('');
  const [hasTracked75Percent, setHasTracked75Percent] = useState(false);

  const calculateReadingTime = (content: string): number => {
    const words = content.trim().split(/\s+/).length;
    const minutes = Math.ceil(words / 200);
    return minutes;
  };

  const generateTableOfContents = (content: string): TocItem[] => {
    const headingRegex = /^(#{2,3})\s+(.+)$/gm;
    const toc: TocItem[] = [];
    let match;
    while ((match = headingRegex.exec(content)) !== null) {
      const level = match[1].length;
      const text = match[2].trim();
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      toc.push({ id, text, level });
    }
    return toc;
  };

  const renderContentWithIds = (content: string): string => {
    return content.replace(/^(#{2,3})\s+(.+)$/gm, (match, hashes, text) => {
      const level = hashes.length;
      const id = text.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      return `<h${level} id="${id}" class="scroll-mt-24">${text.trim()}</h${level}>`;
    });
  };

  const scrollToHeading = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveHeading(id);
    }
  };

  useEffect(() => {
    if (tableOfContents.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveHeading(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -80% 0px' }
    );
    tableOfContents.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, [tableOfContents]);

  useEffect(() => {
    if (!post || hasTracked75Percent) return;
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      if (scrollPercent >= 75 && !hasTracked75Percent) {
        trackEvent('blog_read_completion', {
          post_id: post.id,
          post_title: post.title,
          post_category: post.category,
          scroll_depth: '75%'
        });
        setHasTracked75Percent(true);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [post, hasTracked75Percent]);

  useEffect(() => {
    if (post) {
      trackEvent('blog_post_view', {
        post_id: post.id,
        post_title: post.title,
        post_category: post.category,
        post_author: post.author,
        post_tags: post.tags?.join(', ') || 'none'
      });
    }
  }, [post]);

  useEffect(() => {
    const loadPost = async () => {
      try {
        const posts = await blogService.getPublished();
        const foundPost = posts.find(p => p.slug === postSlug) || posts.find(p => p.id === postSlug);
        if (foundPost) {
          setPost(foundPost);
          const toc = generateTableOfContents(foundPost.content);
          setTableOfContents(toc);
        } else {
          setPost(null);
        }
      } catch (error) {
        console.error('Failed to load blog post:', error);
      } finally {
        setLoading(false);
      }
    };
    loadPost();
  }, [postSlug]);

  useEffect(() => {
    const loadRelatedPosts = async () => {
      if (!post?.id) return;
      try {
        const related = await blogService.getRelatedPosts(post.id, 3);
        setRelatedPosts(related);
      } catch (error) {
        console.error('Failed to load related posts:', error);
      } finally {
        setRelatedLoading(false);
      }
    };
    loadRelatedPosts();
  }, [post]);

  const handleTagClick = (tag: string) => {
    trackEvent('blog_tag_click', {
      tag_name: tag,
      post_id: post?.id,
      post_title: post?.title,
      post_category: post?.category
    });
  };

  const handleCategoryClick = (category: string) => {
    trackEvent('blog_category_click', {
      category_name: category,
      post_id: post?.id,
      post_title: post?.title
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <NavBar />
        <div className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-12 h-12 border-4 border-brand-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-body-text">Loading blog post...</p>
          </div>
        </div>
        <FooterSection />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <NavBar />
        <div className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl font-bold text-navy-dark mb-4">Post Not Found</h1>
            <p className="text-body-text mb-8">The blog post you&apos;re looking for doesn&apos;t exist.</p>
            <Link href="/blog" className="text-brand-purple font-medium hover:underline">
              ← Back to Blog
            </Link>
          </div>
        </div>
        <FooterSection />
      </div>
    );
  }

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: `${SITE_URL}/home-page` },
    { name: 'Blog', url: `${SITE_URL}/blog` },
    { name: post.title, url: `${SITE_URL}/blog/${post.slug}` },
  ]);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    keywords: post.keywords || '',
    author: {
      '@type': 'Person',
      name: post.author,
    },
    datePublished: post.publishedAt || post.createdAt,
    dateModified: post.createdAt,
    publisher: {
      '@type': 'Organization',
      name: 'GoGedit',
      logo: {
        '@type': 'ImageObject',
        url: 'https://img.rocket.new/generatedImages/rocket_gen_img_17d98c087-1771786278408.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/blog/${post.slug}`,
    },
    articleSection: post.category,
    wordCount: post.content.trim().split(/\s+/).length,
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
  };

  const readingTime = calculateReadingTime(post.content);
  const contentWithIds = renderContentWithIds(post.content);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <div className="min-h-screen bg-white">
        <NavBar />
        <div className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-6">
              <Breadcrumb items={[{ label: 'Blog', href: '/blog' }, { label: post.title }]} />
            </div>

            <div className="flex gap-8">
              <article className="flex-1 bg-white max-w-4xl">
                <div className="mb-8">
                  <div className="flex items-center gap-4 mb-4">
                    <Link
                      href={`/blog?category=${encodeURIComponent(post.category)}`}
                      onClick={() => handleCategoryClick(post.category)}
                      className="text-sm font-medium text-brand-purple bg-purple-light px-3 py-1 rounded-full hover:bg-brand-purple hover:text-white transition-colors"
                    >
                      {post.category}
                    </Link>
                    <span className="text-sm text-gray-600 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {readingTime} min read
                    </span>
                  </div>
                  <h1 className="text-4xl md:text-5xl font-bold text-navy-dark mb-6">{post.title}</h1>

                  {post.featuredImageUrl && (
                    <div className="w-full rounded-2xl overflow-hidden mb-6">
                      <img
                        src={post.featuredImageUrl}
                        alt={post.featuredImageAlt || post.title}
                        className="w-full object-cover max-h-96"
                      />
                    </div>
                  )}

                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-6">
                      {post.tags.map((tag, index) => (
                        <Link
                          key={index}
                          href={`/blog?tag=${encodeURIComponent(tag)}`}
                          onClick={() => handleTagClick(tag)}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-light text-brand-purple hover:bg-brand-purple hover:text-white transition-colors"
                        >
                          #{tag}
                        </Link>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-brand-purple rounded-full flex items-center justify-center text-white text-lg font-medium">
                      {post.author.charAt(0)}
                    </div>
                    <div>
                      <p className="text-base font-medium text-navy-dark">{post.author}</p>
                      <p className="text-sm text-gray-500">{formatDate(post.createdAt)}</p>
                    </div>
                  </div>
                </div>

                <div className="prose prose-lg max-w-none mb-12">
                  <div className="text-xl text-body-text mb-8 font-medium">{post.excerpt}</div>
                  <div
                    className="text-body-text leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: contentWithIds }}
                    style={{ whiteSpace: 'pre-wrap' }}
                  />
                </div>

                {post.internalLinks && post.internalLinks.length > 0 && (
                  <div className="border-t border-gray-200 pt-8 mt-8">
                    <h3 className="text-xl font-bold text-navy-dark mb-4">Related Pages</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {post.internalLinks.map((link, index) => (
                        <Link
                          key={index}
                          href={link.url}
                          className="block p-4 border border-gray-200 rounded-lg hover:border-brand-purple hover:shadow-md transition-all"
                        >
                          <div className="flex items-center gap-2">
                            <svg className="w-5 h-5 text-brand-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                            <span className="font-medium text-navy-dark hover:text-brand-purple">{link.title}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {relatedPosts.length > 0 && (
                  <div className="border-t border-gray-200 pt-8 mt-8">
                    <h3 className="text-2xl font-bold text-navy-dark mb-6">Related Articles</h3>
                    <div className="grid md:grid-cols-3 gap-6">
                      {relatedPosts.map((relatedPost) => (
                        <Link
                          key={relatedPost.id}
                          href={`/blog/${relatedPost.slug}`}
                          className="group block bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-brand-purple hover:shadow-lg transition-all"
                        >
                          <div className="p-5">
                            <span className="inline-block text-xs font-medium text-brand-purple bg-purple-light px-2 py-1 rounded-full mb-3">
                              {relatedPost.category}
                            </span>
                            <h4 className="text-lg font-bold text-navy-dark mb-2 group-hover:text-brand-purple transition-colors line-clamp-2">
                              {relatedPost.title}
                            </h4>
                            <p className="text-sm text-body-text line-clamp-3 mb-3">
                              {relatedPost.excerpt}
                            </p>
                            {relatedPost.tags && relatedPost.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {relatedPost.tags.slice(0, 2).map((tag, index) => (
                                  <span key={index} className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                    #{tag}
                                  </span>
                                ))}
                                {relatedPost.tags.length > 2 && (
                                  <span className="text-xs text-gray-500">+{relatedPost.tags.length - 2}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                <div className="border-t border-gray-200 pt-8 mt-8">
                  <Link href="/blog" className="text-brand-purple font-medium hover:underline">
                    ← Back to Blog
                  </Link>
                </div>
              </article>

              {tableOfContents.length > 0 && (
                <aside className="hidden lg:block w-64 flex-shrink-0">
                  <div className="sticky top-24">
                    <div className="bg-purple-light border border-gray-200 rounded-lg p-6">
                      <h3 className="text-lg font-bold text-navy-dark mb-4 flex items-center gap-2">
                        <svg className="w-5 h-5 text-brand-purple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                        Table of Contents
                      </h3>
                      <nav className="space-y-2">
                        {tableOfContents.map((item) => (
                          <button
                            key={item.id}
                            onClick={() => scrollToHeading(item.id)}
                            className={`block w-full text-left text-sm transition-colors ${
                              item.level === 3 ? 'pl-4' : ''
                            } ${
                              activeHeading === item.id
                                ? 'text-brand-purple font-semibold' : 'text-gray-700 hover:text-brand-purple'
                            }`}
                          >
                            {item.text}
                          </button>
                        ))}
                      </nav>
                    </div>
                  </div>
                </aside>
              )}
            </div>
          </div>
        </div>
        <FooterSection />
      </div>
    </>
  );
}
